import { Pedido, PedidoSituacao, PedidoTipo } from 'app/modulos/integracao/pedido/pedido';
import { PedidoPlanilhaLinha } from 'app/modulos/integracao/pedido/pedido-planilha';
import { pedidoNegadaMotivo } from 'app/modulos/integracao/pedido/pedido-status';

// Resumo do pedido para a tela "Meus pedidos". Duas origens caem neste formato: o
// historico do servidor (`pedido/planilha?clienteid=`) e o guardado no aparelho no envio.
export type PedidoResumoItem = {
  nome: string;
  quantidade: number;
  observacoes?: string;
};

export type PedidoResumo = {
  id: string;
  codigo?: string;
  criadoEm: string;
  entrega: boolean;
  mesa: string;
  quantidade: number;
  subtotal: number;
  frete: number;
  // Opcional porque pedido gravado antes do cupom existir nao tem este campo.
  desconto?: number;
  cupom?: string;
  total: number;
  pagamento: string;
  itens: PedidoResumoItem[];
  // So no que vem do servidor. `detalhado` diz se itens e valores ja foram buscados: a
  // listagem nao traz nenhum dos dois.
  tipo?: PedidoTipo;
  situacao?: PedidoSituacao;
  detalhado?: boolean;
  // Por que foi recusado/cancelado, quando a retaguarda disse. So vem com o detalhe.
  motivo?: string;
};

const LIMITE = 20;

// O mais novo primeiro, e sem crescer para sempre: isto vive no storage do navegador.
export function historicoAdiciona(historico: PedidoResumo[], resumo: PedidoResumo): PedidoResumo[] {
  return [resumo, ...(historico || [])].slice(0, LIMITE);
}

export function pedidoResumoComoRecebe(resumo: PedidoResumo): string {
  if (resumo?.mesa) {
    return resumo.mesa;
  }
  if (resumo?.entrega) {
    return 'Entrega';
  }
  // A listagem do servidor nao traz o localizador, so o `tipo`.
  if (resumo?.tipo === PedidoTipo.Mesa) {
    return 'Mesa';
  }
  if (resumo?.tipo === PedidoTipo.Comanda) {
    return 'Comanda';
  }
  return 'Retirada no balcão';
}

// Uma linha so com o que foi pedido, para caber na lista sem abrir o pedido.
export function pedidoResumoItens(resumo: PedidoResumo): string {
  return (resumo?.itens || [])
    .map(item => `${item.quantidade}x ${item.nome}`)
    .join(', ');
}

// ---------- o que vem do servidor ----------

export function pedidoPlanilhaParaResumo(linha: PedidoPlanilhaLinha): PedidoResumo {
  return {
    id: linha?.id || '',
    codigo: linha?.codigo || '',
    criadoEm: linha?.momento || '',
    entrega: linha?.tipo === PedidoTipo.Delivery,
    mesa: '',
    quantidade: 0,
    subtotal: 0,
    frete: 0,
    total: linha?.total || 0,
    pagamento: '',
    itens: [],
    tipo: linha?.tipo,
    situacao: linha?.situacao,
    detalhado: false,
  };
}

// `base` e o resumo que ja estava na tela: o detalhe completa, nunca apaga - o cupom so
// existe no que foi guardado no aparelho.
export function pedidoDetalheParaResumo(pedido: Pedido, base?: PedidoResumo): PedidoResumo {
  const itens = (pedido?.pedidoItens || []).map(item => ({
    // `item.nome` recebe o id do item na gravacao (bug antigo), entao vale o `itemNome`.
    nome: item?.itemNome || item?.item?.nome || '',
    quantidade: item?.quantidade || 0,
    ...(item?.observacoes ? { observacoes: item.observacoes } : {}),
  }));
  return {
    ...(base || ({} as PedidoResumo)),
    id: pedido?.id || base?.id || '',
    codigo: pedido?.codigo || base?.codigo || '',
    criadoEm: pedido?.momento || base?.criadoEm || '',
    entrega: pedido?.tipo === PedidoTipo.Delivery,
    mesa: pedido?.localizador?.nome || base?.mesa || '',
    quantidade: itens.reduce((soma, item) => soma + item.quantidade, 0),
    subtotal: pedido?.subtotal || 0,
    frete: pedido?.frete || 0,
    desconto: pedido?.desconto || 0,
    total: pedido?.total || 0,
    pagamento: pedido?.pedidoPagamentos?.length ? pedido.pedidoPagamentos[0].formaNome : '',
    itens,
    tipo: pedido?.tipo,
    situacao: pedido?.situacao,
    detalhado: true,
    motivo: pedidoNegadaMotivo(pedido),
  };
}

// O servidor manda; o aparelho completa com o que a consulta por cliente nao alcanca.
export function historicoUne(servidor: PedidoResumo[], local: PedidoResumo[]): PedidoResumo[] {
  const unido = [...(servidor || [])];
  const vistos = new Set(unido.map(pedido => pedido.id));
  (local || []).forEach(pedido => {
    if (!vistos.has(pedido.id)) {
      vistos.add(pedido.id);
      unido.push(pedido);
    }
  });
  return unido.sort((a, b) => (b.criadoEm || '').localeCompare(a.criadoEm || ''));
}
